import {currencies, type Currency} from './currencies';

/**
 * Single source of truth for currency data. All consumers should use these helpers rather than
 * importing the raw `currencies` array, so exposure of the Sage-requested currencies can be gated
 * behind the `sage_intacct_requested_currencies_for_forecast` feature flag in one place.
 *
 * Exposure helpers take an explicit `expanded` boolean (the resolved feature-flag value) so they
 * stay pure and testable. Lookup helpers (`getCurrencyByCode`, `isPrefixedByCode`) operate over the
 * full catalog regardless of the flag — a currency that is already in use must still resolve.
 */

export type CurrencyOption = {value: string; label: string};

/** Full catalog — for lookups by code/symbol/placement. Not for populating selectors directly. */
export function getAllCurrencies(): ReadonlyArray<Currency> {
	return currencies;
}

/** Currencies a user is allowed to pick, gated by the feature flag. */
export function getSelectableCurrencies(expanded: boolean): Currency[] {
	return expanded ? [...currencies] : currencies.filter(currency => !currency.sageRequested);
}

export function getCurrencyByCode(code: string | null | undefined): Currency | undefined {
	if (!code) {
		return undefined;
	}
	return currencies.find(currency => currency.code === code);
}

export function isPrefixed(currency: Currency): boolean {
	return currency.placement === 'before';
}

/**
 * Placement lookup by ISO code. Symbols are ambiguous ($, Fr, L, Br map to both placements), so
 * placement must be resolved by code. Returns undefined for unknown codes so callers can fall back.
 */
export function isPrefixedByCode(code?: string | null): boolean | undefined {
	const currency = getCurrencyByCode(code);
	return currency ? isPrefixed(currency) : undefined;
}

export function isPrefixedBySymbol(symbol?: string | null): boolean | undefined {
	if (!symbol) {
		return undefined;
	}

	const matches = currencies.filter(currency => currency.symbol === symbol);
	if (matches.length === 0) {
		return undefined;
	}

	const allBefore = matches.every(currency => currency.placement === 'before');
	const allAfter = matches.every(currency => currency.placement === 'after');
	if (allBefore) {
		return true;
	}
	if (allAfter) {
		return false;
	}
	return undefined; // ambiguous symbol — placement differs across currencies that share it, like $
}

export function formatCurrencyOption(currency: Currency): CurrencyOption {
	return {
		value: currency.code,
		label: `${currency.code} (${currency.symbol}) - ${currency.name}`,
	};
}

/** How a custom field names its currency: "Euro (EUR)". A code the catalog does not list is shown as the code. */
export function formatCustomFieldCurrencyLabel(code: string): string {
	const currency = getCurrencyByCode(code);
	return currency ? `${currency.name} (${currency.code})` : code;
}

/**
 * The currency a new Currency field starts in: the company currency when Forecast's catalog lists it, else none.
 * A company can still be on a retired currency the catalog dropped (HRK, SLL, VEF…), which the custom field
 * service refuses, so the admin picks one instead.
 */
export function customFieldStartingCurrency(companyCurrency: string | null | undefined): string | null {
	return companyCurrency && getCurrencyByCode(companyCurrency) ? companyCurrency : null;
}

/**
 * The currencies a Currency custom field can be created in: the company currency first, then the selectable
 * catalog by name. `include` adds a currency a field already has when the catalog does not offer it, at the end,
 * so the field still shows its own currency and choosing one never reorders the list.
 */
export function getCustomFieldCurrencyOptions(
	companyCurrency: string | null | undefined,
	expanded: boolean,
	include?: string | null
): CurrencyOption[] {
	const offered = getSelectableCurrencies(expanded)
		.sort((a, b) => a.name.localeCompare(b.name))
		.map(currency => currency.code);
	const codes = [customFieldStartingCurrency(companyCurrency), ...offered, include].filter(
		(code, index, all): code is string => !!code && all.indexOf(code) === index
	);
	return codes.map(code => ({value: code, label: formatCustomFieldCurrencyLabel(code)}));
}

/**
 * Currencies currently usable in the company: the company base currency plus any with an exchange
 * rate. These are already activated, so they are returned regardless of the feature flag.
 */
export function getActivatedCurrencies(
	baseCurrency: string | null | undefined,
	exchangeRates: ReadonlyArray<{currency: string}> = []
): Currency[] {
	const codes = new Set<string>();
	if (baseCurrency) {
		codes.add(baseCurrency);
	}
	exchangeRates.forEach(rate => rate?.currency && codes.add(rate.currency));
	return currencies.filter(currency => codes.has(currency.code));
}

/**
 * Currencies that can be added as a new exchange rate: selectable catalog minus the company base
 * currency and minus currencies that already have a rate.
 */
export function getAvailableCurrenciesToAdd(
	baseCurrency: string | null | undefined,
	existingRateCurrencyCodes: ReadonlyArray<string> = [],
	expanded: boolean
): Currency[] {
	const taken = new Set<string>(existingRateCurrencyCodes);
	if (baseCurrency) {
		taken.add(baseCurrency);
	}
	return getSelectableCurrencies(expanded).filter(currency => !taken.has(currency.code));
}
